import { create } from 'zustand';
import type {
  FabricObject,
  Render, HistoryPlugins, IImage, RenderStyle,
} from '@meitu/whee-infinite-canvas';

interface AIToolsCanvasState {
  /** 画布渲染实例 */
  render: Render | null;
  /** 画布光标与交互样式（空格拖移视口等） */
  renderStyle: RenderStyle | null;
  /** 画布上的主图片/视频元素（单图/单视频场景使用） */
  imageTarget: IImage | null;
  /** 画布上的所有媒体元素（多图/多视频场景使用） */
  imageTargets: IImage[];
  /** 历史记录插件实例 */
  historyPlugins: HistoryPlugins | null;
  /** 缩放百分比 5-400 */
  zoomNum: number;
  /** 撤销是否可用 */
  undoEnabled: boolean;
  /** 重做是否可用 */
  redoEnabled: boolean;
  hasMask: boolean;
  currentObjects: FabricObject[];
}

const useAIToolsCanvasStore = create<AIToolsCanvasState>(() => ({
  render: null,
  renderStyle: null,
  imageTarget: null,
  imageTargets: [],
  historyPlugins: null,
  zoomNum: 100,
  undoEnabled: false,
  redoEnabled: false,
  hasMask: false,
  currentObjects: [],
}));

export default useAIToolsCanvasStore;

export const AI_TOOLS_MIN_ZOOM_PERCENT = 5;
export const AI_TOOLS_MAX_ZOOM_PERCENT = 400;

/**
 * 将 AI 单工具画布缩放限制在产品允许的百分比范围内。
 */
export function clampAIToolsZoomPercent(value: number) {
  return Math.max(Math.min(value, AI_TOOLS_MAX_ZOOM_PERCENT), AI_TOOLS_MIN_ZOOM_PERCENT);
}

export function setRender(render: Render | null) {
  useAIToolsCanvasStore.setState(() => ({ render }));
}

export function setRenderStyle(renderStyle: RenderStyle | null) {
  useAIToolsCanvasStore.setState(() => ({ renderStyle }));
}

export function setImageTarget(imageTarget: IImage | null) {
  useAIToolsCanvasStore.setState(() => ({ imageTarget }));
}

export function setImageTargets(imageTargets: IImage[]) {
  useAIToolsCanvasStore.setState(() => ({ imageTargets }));
}

export function setHistoryPlugins(historyPlugins: HistoryPlugins | null) {
  useAIToolsCanvasStore.setState(() => ({ historyPlugins }));
}

/**
 * 同步画布缩放百分比，避免外部插件事件把底栏展示带到边界外。
 */
export function setZoomNum(val: number) {
  useAIToolsCanvasStore.setState(() => ({ zoomNum: clampAIToolsZoomPercent(val) }));
}

export function setHistoryStatus(undoEnabled: boolean, redoEnabled: boolean) {
  useAIToolsCanvasStore.setState(() => ({ undoEnabled, redoEnabled }));
}

export function setHasMask(hasMask: boolean) {
  useAIToolsCanvasStore.setState(() => ({ hasMask }));
}

export function setCurrentObjects(objects: FabricObject[]) {
  useAIToolsCanvasStore.setState(() => ({ currentObjects: objects }));
}

/**
 * 缩放画布
 * @param value 变化量（正数放大/负数缩小）
 */
export function handleZoom(value: number) {
  const { render, zoomNum } = useAIToolsCanvasStore.getState();
  const currentValue = clampAIToolsZoomPercent(zoomNum + value);
  render?._FC.fire('viewport:zoom', { zoom: currentValue / 100 });
  render?.setZoom(currentValue / 100);
}

/**
 * 适应屏幕
 * leftPanelWidth: 左侧面板宽度（默认 352px = 320 + 32 间距）
 */
export function handleFitScreen(leftPanelWidth = 352) {
  const { render } = useAIToolsCanvasStore.getState();
  if (!render) return;
  if (render._FC.getObjects().length === 0) return;
  render.zoomToFitCanvas(AI_TOOLS_MIN_ZOOM_PERCENT / 100, leftPanelWidth, 16, 68, 56);
}

/**
 * 撤销
 */
export function undo() {
  const { historyPlugins } = useAIToolsCanvasStore.getState();
  historyPlugins?.undo();
}

/**
 * redo
 */
export function redo() {
  const { historyPlugins } = useAIToolsCanvasStore.getState();
  historyPlugins?.redo();
}
